// src/pages/SellPage.js
import { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Card, CardHeader, Btn, Input, Select, Table, formatCurrency, formatPct, Badge, PageHeader } from '../components/ui';
import { Calculator, Plus, RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';

const initialForm = {
  customer_id: '', script_id: '', purchase_transaction_id: '',
  trade_date: new Date().toISOString().split('T')[0],
  lot_size: 1, quantity: '', sell_price: '', exchange: 'NSE', segment: 'EQ', remarks: ''
};

export default function SellPage() {
  const [form, setForm] = useState(initialForm);
  const [customers, setCustomers] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [charges, setCharges] = useState(null);
  const [sells, setSells] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [costInfo, setCostInfo] = useState(null);

  useEffect(() => {
    api.get('/masters/customers').then(r => setCustomers(r.data));
    api.get('/masters/scripts').then(r => setScripts(r.data));
    loadSells();
  }, []);

  const loadSells = () => api.get('/trading/sells').then(r => setSells(r.data));

  // Load available purchases when customer+script changes
  useEffect(() => {
    if (form.customer_id && form.script_id) {
      api.get('/trading/purchases', { params: { customer_id: form.customer_id, script_id: form.script_id } })
        .then(r => setPurchases(r.data));
    }
  }, [form.customer_id, form.script_id]);

  // Calculate charges & P&L preview
  useEffect(() => {
    if (form.customer_id && form.quantity && form.sell_price) {
      const cust = customers.find(c => c.id === parseInt(form.customer_id));
      if (cust?.broker_id) {
        api.get('/trading/preview-charges', {
          params: { broker_id: cust.broker_id, quantity: form.quantity, price: form.sell_price, type: 'sell' }
        }).then(r => setCharges(r.data)).catch(() => {});
      }
    } else { setCharges(null); }

    // Cost of acquisition from selected purchase
    if (form.purchase_transaction_id && form.quantity) {
      const pur = purchases.find(p => p.id === parseInt(form.purchase_transaction_id));
      if (pur) {
        const costPerUnit = pur.net_amount / pur.quantity;
        setCostInfo({ costPerUnit, totalCost: costPerUnit * parseInt(form.quantity || 0), buyPrice: pur.buy_price });
      }
    } else { setCostInfo(null); }
  }, [form.customer_id, form.quantity, form.sell_price, form.purchase_transaction_id, customers, purchases]);

  const grossPL = costInfo && charges ? charges.net_amount - costInfo.totalCost : null;
  const plPct = costInfo && grossPL !== null ? (grossPL / costInfo.totalCost) * 100 : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.script_id || !form.quantity || !form.sell_price) {
      toast.error('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      await api.post('/trading/sells', {
        ...form, quantity: parseInt(form.quantity), sell_price: parseFloat(form.sell_price), lot_size: parseInt(form.lot_size),
        purchase_transaction_id: form.purchase_transaction_id || null
      });
      toast.success('Sell transaction recorded!');
      setForm(initialForm);
      setCharges(null);
      setCostInfo(null);
      setShowForm(false);
      loadSells();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to record sell');
    } finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const totalLots = form.lot_size && form.quantity ? (parseInt(form.quantity) / parseInt(form.lot_size)).toFixed(2) : 0;

  return (
    <div>
      <PageHeader
        title="Sell Transactions"
        subtitle="Record sell orders with P&L calculation"
        action={<Btn onClick={() => setShowForm(!showForm)}><Plus size={16} /> New Sell</Btn>}
      />

      {showForm && (
        <Card style={{ marginBottom: 24 }}>
          <CardHeader title="New Sell Entry" subtitle="Review charges and profit/loss before confirming" />
          <form onSubmit={handleSubmit} style={{ padding: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16 }}>
              <Select label="Customer *" value={form.customer_id} onChange={set('customer_id')}>
                <option value="">Select Customer</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.customer_code} - {c.customer_name}</option>)}
              </Select>
              <Select label="Script *" value={form.script_id} onChange={set('script_id')}>
                <option value="">Select Script</option>
                {scripts.map(s => <option key={s.id} value={s.id}>[{s.exchange_code}] {s.script_code} - {s.script_name}</option>)}
              </Select>
              <Select label="Link Purchase (Optional)" value={form.purchase_transaction_id} onChange={set('purchase_transaction_id')}>
                <option value="">Avg. Cost Method</option>
                {purchases.map(p => <option key={p.id} value={p.id}>{p.transaction_no} | {p.quantity} qty @ ₹{p.buy_price} ({p.trade_date?.split('T')[0]})</option>)}
              </Select>
              <Input label="Trade Date *" type="date" value={form.trade_date} onChange={set('trade_date')} />
              <Input label="Lot Size" type="number" min="1" value={form.lot_size} onChange={set('lot_size')} />
              <Input label="Quantity *" type="number" min="1" value={form.quantity} onChange={set('quantity')} placeholder="e.g. 50" />
              <Input label="Sell Price (₹) *" type="number" step="0.01" min="0.01" value={form.sell_price} onChange={set('sell_price')} placeholder="e.g. 300.00" />
              <Select label="Exchange" value={form.exchange} onChange={set('exchange')}>
                <option value="NSE">NSE</option><option value="BSE">BSE</option>
              </Select>
              <Input label="Remarks" value={form.remarks} onChange={set('remarks')} placeholder="Optional" />
            </div>

            {form.quantity && form.lot_size && (
              <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '10px 16px', marginBottom: 16, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 13, color: '#0369a1' }}><strong>Total Lots:</strong> {totalLots}</div>
                {form.sell_price && <div style={{ fontSize: 13, color: '#0369a1' }}><strong>Gross Amount:</strong> {formatCurrency(parseInt(form.quantity) * parseFloat(form.sell_price))}</div>}
                {costInfo && <div style={{ fontSize: 13, color: '#0369a1' }}><strong>Avg Buy Price:</strong> {formatCurrency(costInfo.buyPrice)}</div>}
              </div>
            )}

            {/* Charges + P&L */}
            {charges && (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <Calculator size={16} color="#8b5cf6" />
                  <strong style={{ fontSize: 14, color: '#0f172a' }}>Sell Charges Breakdown</strong>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
                  {[
                    ['Gross Amount', charges.gross_amount],
                    ['Brokerage', charges.brokerage_amount],
                    ['STT (Sell)', charges.stt_amount],
                    ['Txn Charges', charges.transaction_charges],
                    ['GST', charges.gst_amount],
                    ['SEBI Charges', charges.sebi_charges],
                    ['Total Charges', charges.total_charges],
                    ['Net Proceeds', charges.net_amount],
                  ].map(([label, val]) => (
                    <div key={label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500, marginBottom: 3 }}>{label}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{formatCurrency(val)}</div>
                    </div>
                  ))}
                </div>

                {/* P&L Box */}
                {costInfo && grossPL !== null && (
                  <div style={{
                    padding: '16px 20px', borderRadius: 12,
                    background: grossPL >= 0 ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #ef4444, #dc2626)'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                      <div>
                        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginBottom: 4 }}>Cost of Acquisition</div>
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>{formatCurrency(costInfo.totalCost)}</div>
                      </div>
                      <div>
                        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginBottom: 4 }}>Net Proceeds</div>
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>{formatCurrency(charges.net_amount)}</div>
                      </div>
                      <div>
                        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginBottom: 4 }}>Net Profit / Loss</div>
                        <div style={{ color: '#fff', fontWeight: 800, fontSize: 22, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {grossPL >= 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
                          {formatCurrency(grossPL)} ({formatPct(plPct)})
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <Btn type="submit" variant="danger" disabled={loading}>{loading ? 'Saving...' : 'Record Sell'}</Btn>
              <Btn variant="ghost" onClick={() => { setShowForm(false); setForm(initialForm); setCharges(null); }}>Cancel</Btn>
            </div>
          </form>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Sell History"
          subtitle={`${sells.length} transactions`}
          action={<Btn variant="ghost" size="sm" onClick={loadSells}><RefreshCw size={14} /></Btn>}
        />
        <Table
          headers={['Txn No', 'Date', 'Customer', 'Script', 'Lots', 'Qty', 'Sell Price', 'Net Proceeds', 'Cost', 'Net P&L', 'P&L %', 'Days']}
          rows={sells.map(s => {
            const pnl = parseFloat(s.net_profit_loss || 0);
            return [
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{s.transaction_no}</span>,
              s.trade_date?.split('T')[0],
              <div><div style={{ fontWeight: 600 }}>{s.customer_code}</div><div style={{ fontSize: 12, color: '#64748b' }}>{s.customer_name}</div></div>,
              <div><div style={{ fontWeight: 600 }}>{s.script_code}</div><div style={{ fontSize: 12, color: '#64748b' }}>{s.script_name}</div></div>,
              parseFloat(s.total_lots || 0).toFixed(2),
              s.quantity,
              formatCurrency(s.sell_price),
              formatCurrency(s.net_amount),
              formatCurrency(s.cost_of_acquisition),
              <strong style={{ color: pnl >= 0 ? '#10b981' : '#ef4444' }}>{formatCurrency(pnl)}</strong>,
              <span style={{ color: pnl >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>{formatPct(s.profit_loss_pct)}</span>,
              <Badge color={s.holding_days > 365 ? 'green' : 'blue'}>{s.holding_days || 0}d</Badge>
            ];
          })}
          emptyMessage="No sell transactions yet."
        />
      </Card>
    </div>
  );
}
